import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { IntegrationCard as IntegrationCardType } from '../../lib/types';
import { useToast } from '../../components/Toast/ToastContext';
import { SUPPORT_EMAIL } from '../../lib/emails';
import Button from '../../components/Button/Button';
import IntegrationCard from './IntegrationCard';
import ShelterLuvWizardModal from './ShelterLuvWizardModal';
import DisconnectModal from './DisconnectModal';
import ImportPetsModal from './ImportPets';
import ChipsEnrollModal from '../Chips/ChipsEnrollModal';
import { useRescueContext } from '../../lib/RescueContext';
import {
  rescueShelterluvStatusQuery,
  rescueResellerStatsQuery,
  rescueDisableShelterluvMutation,
  RescueShelterluvStatusData,
  RescueResellerStatsData,
  RescueDisableShelterluvData,
} from '../../lib/integrationQueries';
import integrationsData from '../../data/integrations.json';
import { trackShelterLuvSetupStarted, trackShelterLuvDisconnected } from '../../analytics';
import styles from './Integrations.module.scss';

const integrations = integrationsData.integrations as IntegrationCardType[];

export default function Integrations() {
  const { showToast } = useToast();
  const { isVerifiedReseller, orgInfoLoading } = useRescueContext();

  const { data: statusData, refetch: refetchStatus } =
    useQuery<RescueShelterluvStatusData>(rescueShelterluvStatusQuery);
  const { data: statsData, refetch: refetchStats } = useQuery<RescueResellerStatsData>(rescueResellerStatsQuery);

  const [disableShelterluv, { loading: disabling }] = useMutation<RescueDisableShelterluvData>(
    rescueDisableShelterluvMutation,
  );

  // Modal state
  const [wizardOpen, setWizardOpen] = useState(false);
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [enrollModalOpen, setEnrollModalOpen] = useState(false);

  const shelterluvConfig = statusData?.rescueShelterluvStatus;
  const resellerName = statsData?.currentUser?.chipReseller?.name ?? null;
  const chipsImported = statsData?.currentUser?.chipReseller?.stats?.chipsRegisteredOnShelterluv ?? 0;

  const shelterLuvStatus = shelterluvConfig?.enabled
    ? {
        connected: true as const,
        orgName: resellerName,
        lastSync: shelterluvConfig.lastImportAt,
        chipsImported,
        apiKeyMasked: shelterluvConfig.apiKeyEndsWith ? `****${shelterluvConfig.apiKeyEndsWith}` : null,
      }
    : {
        connected: false as const,
        orgName: null,
        lastSync: null,
        chipsImported: 0,
        apiKeyMasked: null,
      };

  function handleConnectSuccess() {
    refetchStatus();
    refetchStats();
    showToast('ShelterLuv connected successfully');
  }

  async function handleDisconnect() {
    try {
      await disableShelterluv({
        refetchQueries: [{ query: rescueShelterluvStatusQuery }, { query: rescueResellerStatsQuery }],
      });
      trackShelterLuvDisconnected();
      setDisconnectOpen(false);
      showToast('ShelterLuv disconnected');
    } catch (err: any) {
      showToast(err.message || 'Failed to disconnect ShelterLuv');
    }
  }

  return (
    <div>
      <h1>Integrations</h1>
      <p className={styles.subtitle}>Connect your rescue management tools to automate workflows.</p>

      <div className={styles.cardGrid}>
        {integrations.map((integration) => (
          <IntegrationCard
            key={integration.id}
            integration={integration}
            shelterLuvStatus={integration.id === 'shelterluv' ? shelterLuvStatus : undefined}
            onConnect={() => {
              if (integration.id === 'import-manually') {
                // Drop the click while the org-info query is still in flight.
                // `isVerifiedReseller` defaults to `false` during loading, so
                // a fast-clicker on first paint would otherwise see the
                // enrollment modal even when they're a verified partner.
                if (orgInfoLoading) {
                  return;
                }
                if (!isVerifiedReseller) {
                  setEnrollModalOpen(true);
                  return;
                }
                setImportOpen(true);
                return;
              }
              if (integration.id === 'shelterluv') {
                trackShelterLuvSetupStarted();
                setWizardOpen(true);
              }
            }}
            onDisconnect={() => setDisconnectOpen(true)}
          />
        ))}
      </div>

      <div className={styles.requestSection}>
        <h3 className={styles.requestTitle}>Need a different integration?</h3>
        <p className={styles.requestDescription}>
          Let us know what tools you use and we&apos;ll prioritize accordingly.
        </p>
        <a href={`mailto:${SUPPORT_EMAIL}?subject=Integration%20Request`}>
          <Button variant="secondary" size="sm" className={styles.requestBtn}>
            Request Integration
          </Button>
        </a>
      </div>

      <ShelterLuvWizardModal open={wizardOpen} onClose={() => setWizardOpen(false)} onSuccess={handleConnectSuccess} />
      <DisconnectModal
        open={disconnectOpen}
        onClose={() => setDisconnectOpen(false)}
        onConfirm={handleDisconnect}
        loading={disabling}
      />
      <ImportPetsModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onSuccess={() => {
          showToast('CSV upload received');
        }}
      />
      <ChipsEnrollModal open={enrollModalOpen} onClose={() => setEnrollModalOpen(false)} />
    </div>
  );
}
